import PropTypes from 'prop-types';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useRBACStore } from '../../../stores/useRBACStore';
import { useTransition } from 'react';
import moreSquare from './images/more-square.svg';
import styles from './NavigationFooter.module.scss';

const NavigationFooter = ({ menus }) => {
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const hasPrivilege = useRBACStore((s) => s.hasPrivilege);

  // Filter menus based on privileges
  const filteredMenus = menus.filter((menu) => {
    // Check if menu itself has privilege requirement
    if (menu.privilege && !hasPrivilege(menu.privilege)) {
      return false;
    }

    // If menu has children, check if at least one child has privilege
    if (menu.children && menu.children.length > 0) {
      const visibleChildren = menu.children.filter(
        (child) => !child.privilege || hasPrivilege(child.privilege)
      );
      // Only show parent menu if at least one child is visible
      return visibleChildren.length > 0;
    }

    // Menu has no privilege requirement or no children, show it
    return true;
  });

  // Include moreIcon in the menus array
  const extendedMenus = showMore
    ? [...filteredMenus, { title: 'Less', src: moreSquare, navigate: 'more' }]
    : [
        ...filteredMenus.slice(0, 4),
        { title: 'More', src: moreSquare, navigate: 'more' },
      ];

  return (
    <div
      className={`z-10 grid grid-cols-5 w-full bg-white px-3 max-xs:px-2 max-xs:pb-4 pb-6 duration-300 fixed bottom-0 border border-[#D1D9E8]`}
    >
      {extendedMenus.map((menu, index) => (
        <div key={index}>
          {menu.navigate !== 'more' ? (
            <NavLink
              key={index}
              to={menu.navigate}
              onClick={(e) => {
                e.preventDefault();
                setShowMore(false);
                startTransition(() => {
                  navigate(menu.navigate);
                });
              }}
              className={({ isActive, isPending }) =>
                `flex max-xs:mt-2 mt-3 flex-col cursor-pointer text-[#858E95] text-base items-center gap-1  
              ${menu.gap ? 'mt-9' : ''} ${
                index === 0 && 'bg-light-white'
              } ${isPending ? 'pending' : isActive ? `active ${styles.active}` : ''}`
              }

              // className={`flex mt-3 flex-col cursor-pointer text-[#858E95] text-base items-center gap-1
              // ${menu.gap ? 'mt-9' : ''} ${
              //   index === 0 && 'bg-light-white'
              // }`}
            >
              {menu?.src}
              <span className={`origin-left duration-200 text-[#858E95]`}>
                {menu.title}
              </span>
            </NavLink>
          ) : (
            <div
              key={index}
              className={`flex flex-col cursor-pointer text-[#858E95] text-base items-center gap-1 
              ${menu.gap ? 'mt-9' : 'mt-3 max-xs:mt-2'} ${index === 0 && 'bg-light-white'}`}
              onClick={() => setShowMore(!showMore)}
            >
              <img
                src={menu.src}
                className="max-xs:h-[20px] max-xs:w-[20px]"
                alt={menu?.title}
              />
              <span className={`origin-left duration-200 text-[#858E95]`}>
                {menu.title}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

NavigationFooter.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      navigate: PropTypes.string,
      src: PropTypes.node,
      title: PropTypes.string,
      gap: PropTypes.bool,
      children: PropTypes.arrayOf(PropTypes.object),
      onClick: PropTypes.func,
      activeMatch: PropTypes.arrayOf(PropTypes.string), // Array of regex patterns for custom matching
      privilege: PropTypes.string, // Privilege required to view this menu item
    })
  ),
};

export default NavigationFooter;
